import ReportService from "../../../services/reports/Report.service";
import YoungworldReportService from "../../../services/reports/YoungworldReport.service";
import { useState, useEffect, useRef, useMemo } from "react";
import { NextQuarterRawData } from "./YoungWorldTypes";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";
import ReportTable from "../ReportTable.component";
import EpochMonthService from "../../../services/EpochMonth.service";
import * as XLSX from "xlsx";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import type { EmployeeFilter } from "../../../services/reports/YoungworldReport.service";

interface YoungworldNextQuarterReportProps {
  clientId: string | number;
  epochMonth?: number | null;
}

interface YoungworldNextQuarterParsedData extends BaseReportParsedData {
  categorySortOrder?: number | null;
  month1ThreeAgo: MoneyDataItem;
  month1Ly: MoneyDataItem;
  month1Plan: MoneyDataItem;
  month1Otr: MoneyDataItem;
  month1Oo: MoneyDataItem;
  month1Eom: MoneyDataItem;
  month1Otb: MoneyDataItem;
  month2ThreeAgo: MoneyDataItem;
  month2Ly: MoneyDataItem;
  month2Plan: MoneyDataItem;
  month2Otr: MoneyDataItem;
  month2Oo: MoneyDataItem;
  month2Eom: MoneyDataItem;
  month2Otb: MoneyDataItem;
  month3ThreeAgo: MoneyDataItem;
  month3Ly: MoneyDataItem;
  month3Plan: MoneyDataItem;
  month3Otr: MoneyDataItem;
  month3Oo: MoneyDataItem;
  month3Eom: MoneyDataItem;
  month3Otb: MoneyDataItem;
}

const HIERARCHY_OPTIONS = {
  "Company": ["category", "locationCode"] as const,
  "By Location": ["locationCode"] as const,
  "By Category": ["category"] as const,
  "Category by Class by Location": ["category", "classCode", "locationCode"] as const,
};

function num(v: any): number { return (v === undefined || v === null || isNaN(Number(v))) ? 0 : Number(v); }

function monthLabel(epoch: number): string {
  const full = EpochMonthService.epochMonthToMMMM(epoch);
  return full.substring(0, 3);
}

function parseRaw(rows: NextQuarterRawData[], quarterEpochMonth: number): YoungworldNextQuarterParsedData[] {
  // Group by locationCode|classCode|category
  const map = new Map<string, { months: Record<number, NextQuarterRawData> }>();
  rows.forEach(r => {
    const key = `${r.locationCode}|${r.classCode}|${r.category || ''}`;
    if (!map.has(key)) map.set(key, { months: {} });
    const entry = map.get(key)!;
    entry.months[r.epochMonthSpan] = r;
  });

  const parsed: YoungworldNextQuarterParsedData[] = [];
  map.forEach((entry) => {
    const month1 = entry.months[quarterEpochMonth];
    const month2 = entry.months[quarterEpochMonth + 1];
    const month3 = entry.months[quarterEpochMonth + 2];

    // Use first available row to fill identifying fields
    const firstRow = month1 || month2 || month3;
    if (!firstRow) return; // safety

    parsed.push({
      locationCode: firstRow.locationCode,
      locationName: firstRow.locationName,
      classCode: firstRow.classCode,
      className: firstRow.className,
      category: firstRow.category ?? 'N/A',
      categorySortOrder: (firstRow as any).categorySortOrder ?? null,
      parentClientClassCode: null,
      trend: 0,
      month1ThreeAgo: new MoneyDataItem(month1 ? num(month1.salesThreeAgo) : 0),
      month1Ly: new MoneyDataItem(month1 ? num(month1.salesLastYear) : 0),
      month1Plan: new MoneyDataItem(month1 ? num(month1.salesPlan) : 0),
      month1Otr: new MoneyDataItem(month1 ? num(month1.inflowPlanRetail) : 0),
      month1Oo: new MoneyDataItem(month1 ? num(month1.onOrderRetailMtd) : 0),
      month1Eom: new MoneyDataItem(month1 ? num(month1.inventoryBomPlanNm) : 0),
      month1Otb: new MoneyDataItem(month1 ? num(month1.otbRetail) : 0),
      month2ThreeAgo: new MoneyDataItem(month2 ? num(month2.salesThreeAgo) : 0),
      month2Ly: new MoneyDataItem(month2 ? num(month2.salesLastYear) : 0),
      month2Plan: new MoneyDataItem(month2 ? num(month2.salesPlan) : 0),
      month2Otr: new MoneyDataItem(month2 ? num(month2.inflowPlanRetail) : 0),
      month2Oo: new MoneyDataItem(month2 ? num(month2.onOrderRetailMtd) : 0),
      month2Eom: new MoneyDataItem(month2 ? num(month2.inventoryBomPlanNm) : 0),
      month2Otb: new MoneyDataItem(month2 ? num(month2.otbRetail) : 0),
      month3ThreeAgo: new MoneyDataItem(month3 ? num(month3.salesThreeAgo) : 0),
      month3Ly: new MoneyDataItem(month3 ? num(month3.salesLastYear) : 0),
      month3Plan: new MoneyDataItem(month3 ? num(month3.salesPlan) : 0),
      month3Otr: new MoneyDataItem(month3 ? num(month3.inflowPlanRetail) : 0),
      month3Oo: new MoneyDataItem(month3 ? num(month3.onOrderRetailMtd) : 0),
      month3Eom: new MoneyDataItem(month3 ? num(month3.inventoryBomPlanNm) : 0),
      month3Otb: new MoneyDataItem(month3 ? num(month3.otbRetail) : 0),
    });
  });

  return parsed;
}

// Helper to calculate quarter start epoch month
function getQuarterEpochMonth(epochMonth: number, quartersAhead: number = 1): number {
  const date = EpochMonthService.epochMonthToDate(epochMonth);
  const month = date.getMonth() + 1; // 1-12
  let year = date.getFullYear();

  let quarterMonth: number;
  
  // Determine current quarter
  if ([12, 1, 2].includes(month)) {
    quarterMonth = 12;
    if (month !== 12) year -= 1;
  } else if ([3, 4, 5].includes(month)) {
    quarterMonth = 3;
  } else if ([6, 7, 8].includes(month)) {
    quarterMonth = 6;
  } else {
    quarterMonth = 9;
  }

  // Add quarters ahead
  for (let i = 0; i < quartersAhead; i++) {
    quarterMonth += 3;
    if (quarterMonth > 12) {
      quarterMonth = 3;
      year += 1;
    }
  }

  return EpochMonthService.dateToEpochMonth(new Date(year, quarterMonth - 1, 1));
}

// Helper to format quarter label (e.g., "Mar-May")
function formatQuarterLabel(quarterStartEpoch: number): string {
  const month1 = monthLabel(quarterStartEpoch);
  const month3 = monthLabel(quarterStartEpoch + 2);
  return `${month1}-${month3}`;
}

const YoungworldNextQuarterReport = ({ clientId, epochMonth }: YoungworldNextQuarterReportProps) => {
  const [selectedEpochMonth, setSelectedEpochMonth] = useState<number>(epochMonth ?? EpochMonthService.getTodayEpochMonth());
  const [selectedQuarterAhead, setSelectedQuarterAhead] = useState<number>(1); // 1 = next quarter, 2 = quarter after
  const [employee, setEmployee] = useState<EmployeeFilter>('none');
  const rawRef = useRef<NextQuarterRawData[]>([]);
  const [parsed, setParsed] = useState<YoungworldNextQuarterParsedData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hierarchyKey, setHierarchyKey] = useState<keyof typeof HIERARCHY_OPTIONS>("Company");
  const hierarchy = HIERARCHY_OPTIONS[hierarchyKey] as readonly string[];

  const quarterEpochMonth = useMemo(() => getQuarterEpochMonth(selectedEpochMonth, selectedQuarterAhead), [selectedEpochMonth, selectedQuarterAhead]);

  // Generate quarter options
  const quarterOptions = useMemo(() => {
    const nextQuarter = getQuarterEpochMonth(selectedEpochMonth, 1);
    const quarterAfter = getQuarterEpochMonth(selectedEpochMonth, 2);
    return [
      { value: 1, label: `Next Quarter (${formatQuarterLabel(nextQuarter)})`, epoch: nextQuarter },
      { value: 2, label: `Quarter After (${formatQuarterLabel(quarterAfter)})`, epoch: quarterAfter },
    ];
  }, [selectedEpochMonth]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await YoungworldReportService.getFutureQuarterReportData({ 
          clientId, 
          epochMonth: selectedEpochMonth,
          quarterEpochStart: quarterEpochMonth
        });
        const rows: NextQuarterRawData[] = Array.isArray((data as any)?.data) ? (data as any).data : (data as NextQuarterRawData[]);
        rawRef.current = rows || [];
        const _parsed = parseRaw(rawRef.current, quarterEpochMonth);
        setParsed(_parsed);
      } catch (e) {
        console.error(e);
        setParsed([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, selectedEpochMonth, quarterEpochMonth]);

  // Default hierarchy to Category by Class by Location when an employee filter is active
  useEffect(() => {
    if (employee !== 'none') {
      setHierarchyKey('Category by Class by Location');
    }
  }, [employee]);

  const filtered = useMemo(() => {
    // Apply employee class code filter first
    const classCodes = YoungworldReportService.getEmployeeClassCodes(employee);
    const employeeFiltered = (classCodes.length > 0) ? parsed.filter(p => classCodes.includes(Number(p.classCode))) : parsed;
    return ReportService.sortAndFilterData(employeeFiltered, [...hierarchy], [], [], []);
  }, [parsed, hierarchy, employee]);

  const monthEpochs = useMemo(() => [quarterEpochMonth, quarterEpochMonth + 1, quarterEpochMonth + 2], [quarterEpochMonth]);

  const columns = useMemo(() => {
    const hierarchyColumns = hierarchy.map(h => ({ name: h === 'locationCode' ? 'Location' : h === 'category' ? 'Category' : h === 'classCode' ? 'Class' : h, sortable: false }));
    const mLabels = monthEpochs.map(e => monthLabel(e));
    
    const cols: { name: string; sortable: boolean }[] = [
      ...hierarchyColumns,
      { name: `${mLabels[0]} 3Ago`, sortable: false },
      { name: `${mLabels[0]} LY`, sortable: false },
      { name: `${mLabels[0]} Plan`, sortable: false },
      { name: `${mLabels[0]} OTR`, sortable: false },
      { name: `${mLabels[0]} OO`, sortable: false },
      { name: `${mLabels[0]} EOM`, sortable: false },
      { name: `${mLabels[0]} OTB`, sortable: false },
      { name: `${mLabels[1]} 3Ago`, sortable: false },
      { name: `${mLabels[1]} LY`, sortable: false },
      { name: `${mLabels[1]} Plan`, sortable: false },
      { name: `${mLabels[1]} OTR`, sortable: false },
      { name: `${mLabels[1]} OO`, sortable: false },
      { name: `${mLabels[1]} EOM`, sortable: false },
      { name: `${mLabels[1]} OTB`, sortable: false },
      { name: '2 Month OTB', sortable: false },
      { name: `${mLabels[2]} 3Ago`, sortable: false },
      { name: `${mLabels[2]} LY`, sortable: false },
      { name: `${mLabels[2]} Plan`, sortable: false },
      { name: `${mLabels[2]} OTR`, sortable: false },
      { name: `${mLabels[2]} OO`, sortable: false },
      { name: `${mLabels[2]} EOM`, sortable: false },
      { name: `${mLabels[2]} OTB`, sortable: false },
      { name: '3 Month OTB', sortable: false },
    ];
    return cols;
  }, [hierarchy, monthEpochs]);

  const tableData = useMemo(() => {
    return filtered.map(r => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...hierarchy]);
      
      const m1Otb = num(r.month1Otb.value);
      const m2Otb = num(r.month2Otb.value);
      const m3Otb = num(r.month3Otb.value);
      
      const twoMonthOtb = m1Otb + m2Otb;
      const threeMonthOtb = m1Otb + m2Otb + m3Otb;
      
      return [
        ...labels,
        // Month 1
        ReportService.formatCurrency(num(r.month1ThreeAgo.value)),
        ReportService.formatCurrency(num(r.month1Ly.value)),
        ReportService.formatCurrency(num(r.month1Plan.value)),
        ReportService.formatCurrency(num(r.month1Otr.value)),
        ReportService.formatCurrency(num(r.month1Oo.value)),
        ReportService.formatCurrency(num(r.month1Eom.value)),
        ReportService.formatCurrency(m1Otb),
        // Month 2
        ReportService.formatCurrency(num(r.month2ThreeAgo.value)),
        ReportService.formatCurrency(num(r.month2Ly.value)),
        ReportService.formatCurrency(num(r.month2Plan.value)),
        ReportService.formatCurrency(num(r.month2Otr.value)),
        ReportService.formatCurrency(num(r.month2Oo.value)),
        ReportService.formatCurrency(num(r.month2Eom.value)),
        ReportService.formatCurrency(m2Otb),
        // 2 Month OTB
        ReportService.formatCurrency(twoMonthOtb),
        // Month 3
        ReportService.formatCurrency(num(r.month3ThreeAgo.value)),
        ReportService.formatCurrency(num(r.month3Ly.value)),
        ReportService.formatCurrency(num(r.month3Plan.value)),
        ReportService.formatCurrency(num(r.month3Otr.value)),
        ReportService.formatCurrency(num(r.month3Oo.value)),
        ReportService.formatCurrency(num(r.month3Eom.value)),
        ReportService.formatCurrency(m3Otb),
        // 3 Month OTB
        ReportService.formatCurrency(threeMonthOtb),
      ];
    });
  }, [filtered, hierarchy]);

  const exportToExcel = () => {
    try {
      const header = columns.map(c => c.name);
      const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'YoungworldFutureQuarter');
      const quarterLabel = formatQuarterLabel(quarterEpochMonth);
      const fileName = `Youngworld_FutureQuarter_${quarterLabel}_${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-[1700px]">
      <h2 className="text-2xl font-bold mb-2">
        {`${EpochMonthService.epochMonthToMMMYY(selectedEpochMonth)} Youngworld Future Quarter (${formatQuarterLabel(quarterEpochMonth)}) - ${hierarchyKey}`}
      </h2>
      <div className="w-full flex flex-wrap items-center justify-end pr-4 mb-2 gap-2">
        <div className="flex items-center gap-2 mr-auto pl-2">
          <label className="text-sm">Month:</label>
          <MonthSelector epochMonth={selectedEpochMonth} setEpochMonth={(v: number) => setSelectedEpochMonth(v)} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Quarter:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={selectedQuarterAhead}
            onChange={(e) => setSelectedQuarterAhead(Number(e.target.value))}
          >
            {quarterOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Hierarchy:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={hierarchyKey}
            onChange={(e) => setHierarchyKey(e.target.value as keyof typeof HIERARCHY_OPTIONS)}
          >
            {Object.keys(HIERARCHY_OPTIONS).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Employee:</label>
          <select
            className="h-9 px-2 border rounded-md text-sm"
            value={employee}
            onChange={(e) => setEmployee(e.target.value as EmployeeFilter)}
          >
            <option value="none">All</option>
            <option value="Lisa">Lisa</option>
            <option value="Hector">Hector</option>
            <option value="Aaron">Aaron</option>
          </select>
        </div>
        <button
          onClick={exportToExcel}
          className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
        >
          Download Excel
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="w-full h-full">
          <ReportTable
            columns={columns}
            data={tableData}
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
            stickyHeader={true}
          />
        </div>
      )}
    </div>
  );
};

export default YoungworldNextQuarterReport;
